import React, { useState } from "react";
import { useQuery, gql } from "@apollo/client";
import Loader from "../../Loader";
import CategoryDistributionDrawer from "./CategoryDistributionDrawer";

const GET_INVENTORY_SCAN_ANALYTICS = gql`
  query getInventoryScanAnalytics($startDate: String!, $endDate: String!, $employeeId: ID) {
    getInventoryScanAnalytics(startDate: $startDate, endDate: $endDate, employeeId: $employeeId) {
      status
      data {
        totalCompletedOrders
        serviceScannedNull
        serviceScannedNotNull
        inventoryScanCompleted
        inventoryScanPending
        forceScanTrueStats {
          orderCount
          itemStats {
            totalRequiredItems
            scannedByAdmin
            scannedByEmployee
            notScanned
            notRequiredItems
            notRequiredScanned
            notRequiredNotScanned
            notScannedCategoryDistribution {
              categoryName
              count
            }
            notRequiredNotScannedCategoryDistribution {
              categoryName
              count
            }
          }
        }
        forceScanFalseStats {
          orderCount
          itemStats {
            totalRequiredItems
            scannedByAdmin
            scannedByEmployee
            notScanned
            notRequiredItems
            notRequiredScanned
            notRequiredNotScanned
            notScannedCategoryDistribution {
              categoryName
              count
            }
            notRequiredNotScannedCategoryDistribution {
              categoryName
              count
            }
          }
        }
      }
    }
  }
`;

const StatCard = ({ title, value, subtext, color = "gray" }) => (
  <div className="bg-[#2C2C2E] p-4 rounded-xl flex flex-col justify-center">
    <div className="text-gray-400 text-sm font-medium">{title}</div>
    <div className={`text-2xl font-bold mt-1 text-${color}-400`}>{value}</div>
    {subtext && <div className="text-xs text-gray-500 mt-1">{subtext}</div>}
  </div>
);

const ForceScanDetails = ({ title, stats, onDrawerOpen }) => {
  const { orderCount, itemStats } = stats;
  return (
    <div className="bg-[#232325] p-5 rounded-xl border border-gray-700 w-full">
      <div className="text-white font-bold mb-3 border-b border-gray-700 pb-2">{title}</div>
      <div className="grid grid-cols-3 gap-4 mb-4">
        <div>
          <div className="text-gray-400 text-xs">Total Orders</div>
          <div className="text-lg text-white font-semibold">{orderCount}</div>
        </div>
        <div>
          <div className="text-gray-400 text-xs">Required Items</div>
          <div className="text-lg text-white font-semibold">{itemStats.totalRequiredItems}</div>
        </div>
        <div>
          <div className="text-gray-400 text-xs">Not Required</div>
          <div className="text-lg text-gray-500 font-semibold">{itemStats.notRequiredItems}</div>
        </div>
      </div>
      
      <div className="grid grid-cols-2 gap-4">
        {/* Required Items Block */}
        <div className="bg-[#1C1C1E] p-3 rounded-lg">
          <div className="text-gray-400 text-xs mb-2">Required Items Progress</div>
          <div className="space-y-2">
            <div className="flex justify-between text-xs">
              <span className="text-green-400">Employee Scanned</span>
              <span className="text-white font-medium">{itemStats.scannedByEmployee}</span>
            </div>
            <div className="w-full bg-gray-800 rounded-full h-1 mb-2">
              <div className="bg-green-500 h-1 rounded-full" style={{ width: `${itemStats.totalRequiredItems ? (itemStats.scannedByEmployee / itemStats.totalRequiredItems) * 100 : 0}%` }}></div>
            </div>

            <div className="flex justify-between text-xs">
              <span className="text-blue-400">Admin Scanned</span>
              <span className="text-white font-medium">{itemStats.scannedByAdmin}</span>
            </div>
            <div className="w-full bg-gray-800 rounded-full h-1 mb-2">
              <div className="bg-blue-500 h-1 rounded-full" style={{ width: `${itemStats.totalRequiredItems ? (itemStats.scannedByAdmin / itemStats.totalRequiredItems) * 100 : 0}%` }}></div>
            </div>

            <div className="flex justify-between text-xs items-center">
              <span className="text-red-400">Not Scanned</span>
              {itemStats.notScanned > 0 ? (
                <button
                  onClick={() => onDrawerOpen(title + " - Required Not Scanned", itemStats.notScannedCategoryDistribution)}
                  className="bg-red-500/20 text-red-400 px-2 py-0.5 rounded text-xs font-bold hover:bg-red-500/30 transition-colors"
                >
                  {itemStats.notScanned} (View)
                </button>
              ) : (
                <span className="text-white font-medium">{itemStats.notScanned}</span>
              )}
            </div>
            <div className="w-full bg-gray-800 rounded-full h-1">
              <div className="bg-red-500 h-1 rounded-full" style={{ width: `${itemStats.totalRequiredItems ? (itemStats.notScanned / itemStats.totalRequiredItems) * 100 : 0}%` }}></div>
            </div>
          </div>
        </div>

        {/* Not Required Items Block */}
        <div className="bg-[#1C1C1E] p-3 rounded-lg border border-dashed border-gray-700">
          <div className="text-gray-500 text-xs mb-2">Not-Required Items Details</div>
          <div className="space-y-2">
            <div className="flex justify-between text-xs">
              <span className="text-gray-300">Scanned Anyway</span>
              <span className="text-white font-medium">{itemStats.notRequiredScanned}</span>
            </div>
            <div className="w-full bg-gray-800 rounded-full h-1 mb-2">
              <div className="bg-gray-400 h-1 rounded-full" style={{ width: `${itemStats.notRequiredItems ? (itemStats.notRequiredScanned / itemStats.notRequiredItems) * 100 : 0}%` }}></div>
            </div>

            <div className="flex justify-between text-xs items-center">
              <span className="text-gray-500">Not Scanned</span>
              {itemStats.notRequiredNotScanned > 0 ? (
                <button
                  onClick={() => onDrawerOpen(title + " - Not Required Not Scanned", itemStats.notRequiredNotScannedCategoryDistribution)}
                  className="bg-gray-600/30 text-gray-400 px-2 py-0.5 rounded text-xs font-bold hover:bg-gray-600/50 transition-colors"
                >
                  {itemStats.notRequiredNotScanned} (View)
                </button>
              ) : (
                <span className="text-white font-medium">{itemStats.notRequiredNotScanned}</span>
              )}
            </div>
            <div className="w-full bg-gray-800 rounded-full h-1">
              <div className="bg-gray-600 h-1 rounded-full" style={{ width: `${itemStats.notRequiredItems ? (itemStats.notRequiredNotScanned / itemStats.notRequiredItems) * 100 : 0}%` }}></div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const InventoryScanAnalyticsWidget = ({ filters }) => {
  const [drawerConfig, setDrawerConfig] = useState({ isOpen: false, title: "", data: [] });

  const { data, loading, error } = useQuery(GET_INVENTORY_SCAN_ANALYTICS, {
    variables: { 
      startDate: filters.startDate, 
      endDate: filters.endDate,
      employeeId: filters.employeeId || null
    },
    fetchPolicy: "network-only",
  });

  if (loading) return <div className="p-6"><Loader /></div>;
  if (error) return <div className="text-red-500 p-6">Error loading analytics</div>;

  const analytics = data?.getInventoryScanAnalytics?.data;
  if (!analytics) return null;

  return (
    <div className="bg-[#1C1C1E] p-6 rounded-2xl shadow-lg border border-gray-800">
      <h3 className="text-[#FBB033] font-bold text-xl mb-6">Inventory Scan Analytics</h3>
      
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <StatCard title="Total Completed Orders" value={analytics.totalCompletedOrders} color="white" />
        <StatCard title="Skipped Scanning" value={analytics.serviceScannedNull} color="red" />
        <StatCard title="Admin Verified Inventories" value={analytics.inventoryScanCompleted} color="green" />
        <StatCard title="Admin Verification Pending" value={analytics.inventoryScanPending} color="yellow" />
      </div>

      <div className="flex flex-col md:flex-row space-y-4 md:space-y-0 md:space-x-4">
        <ForceScanDetails 
          title="Force Scan: FALSE (Valid Scans)" 
          stats={analytics.forceScanFalseStats} 
          onDrawerOpen={(title, data) => setDrawerConfig({ isOpen: true, title, data })}
        />
        <ForceScanDetails 
          title="Force Scan: TRUE (Bypassed)" 
          stats={analytics.forceScanTrueStats} 
          onDrawerOpen={(title, data) => setDrawerConfig({ isOpen: true, title, data })}
        />
      </div>

      <CategoryDistributionDrawer 
        isOpen={drawerConfig.isOpen}
        title={drawerConfig.title}
        data={drawerConfig.data}
        onClose={() => setDrawerConfig({ isOpen: false, title: "", data: [] })}
      />
    </div>
  );
};

export default InventoryScanAnalyticsWidget;
